import { Box, CircularProgress } from '@material-ui/core';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { useAuthorizeJiraServerPluginUser } from './jira-server.hooks';
import environment from '../../environments';

const targetOrigin = environment.jiraUrl || '*';

export enum JiraServerMessageType {
  Init = 'Init',
  Search = 'Search'
}

export enum JiraServerRequestType {
  Search = 'Search'
}

export interface JiraServerUser {
  emailAddress: string;
  name: string;
}

export interface JiraServerRequest<T> {
  id: number;
  type: JiraServerRequestType;
  callback: T;
}

export interface JiraServerState {
  isJiraServer: boolean;
  baseUrl: string;
  jiraUser?: JiraServerUser;
  request: (type: JiraServerRequestType, params: any, callback: any) => void;
}

export const JiraServerContext = createContext<JiraServerState>(null!);

export const useJiraServerContext = () => {
  const context = useContext(JiraServerContext);
  if (!context) {
    throw new Error(
      'useJiraServerContext must be used within the JiraServerProvider component'
    );
  }
  return context;
};

export const JiraServerProvider: React.FC = ({ children }) => {
  const [isJiraServer, setIsJiraServer] = useState(false);
  const [baseUrl, setBaseUrl] = useState('');
  const [jiraUser, setJiraUser] = useState<JiraServerUser>();
  const requestsRef = useRef<JiraServerRequest<any>[]>([]);
  const indexRef = useRef(0);
  const { isLoading, authorizeUser } = useAuthorizeJiraServerPluginUser();
  /**
   * Send request to Jira Plugin
   */
  const request = useCallback(
    (type: JiraServerRequestType, params: any, callback: any) => {
      indexRef.current++;
      const id = indexRef.current;
      requestsRef.current.push({ id, type, callback });
      window.parent.postMessage({ id, type, params }, targetOrigin);
    },
    []
  );

  // Handle response from Jira Plugin
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      switch (event.data?.type) {
        case JiraServerMessageType.Init:
          console.debug('Jira Server init received', event.data);
          const init = event.data.init;
          setIsJiraServer(init.isJira);
          setJiraUser(init.user);
          setBaseUrl(init['base-url']);
          authorizeUser(init.user, init.qmsToken);
          break;
        case JiraServerMessageType.Search:
          console.debug('Jira Server Search received', event.data);
          const id = event.data.id;
          const index = requestsRef.current.findIndex(r => r.id === id);
          if (index === -1) {
            console.error(`Request id ${id} not found`, event.data);
            return;
          }
          const request = requestsRef.current[index];
          requestsRef.current.splice(index, 1);
          request.callback(event.data?.results);
          break;
      }
    };
    window.addEventListener('message', onMessage);
    if (window !== window.parent) {
      console.info(
        'Notifying all parent windows that App is completely mounted'
      );
      window.parent.postMessage('mounted', targetOrigin);
    }
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [authorizeUser]);

  // If Jira redirect URL is specified in config then check to see if
  // user needs to redirected.
  const allowedPaths = [
    '/password/recover',
    '/password/reset',
    '/suppliers/survey'
  ];
  if (
    environment.jiraRedirectUrl &&
    window === window.parent &&
    !allowedPaths.includes(window.location.pathname) &&
    !window.location.search.includes('no-redirect')
  ) {
    console.log('redirect to jira server');
    window.location.href = environment.jiraRedirectUrl;
  }

  return (
    <JiraServerContext.Provider
      value={{
        isJiraServer,
        baseUrl,
        jiraUser,
        request
      }}
    >
      {isLoading ? (
        <Box
          display='flex'
          width='100%'
          height='100%'
          justifyContent='center'
          alignItems='center'
        >
          <CircularProgress />
        </Box>
      ) : (
        children
      )}
    </JiraServerContext.Provider>
  );
};
